import { useMemo, useState } from 'react';
import { Badge } from '../common/Badge';
import type {
  ClusterConfidence,
  DuplicateCluster,
  ScoredCandidate,
} from '../../types/referenceDedup';

/** Columns the table can be sorted by. */
type SortKey =
  | 'brandId'
  | 'identityKey'
  | 'docCount'
  | 'confidence'
  | 'bookmarks'
  | 'wishlists'
  | 'curatedCollections'
  | 'submissionsIndex';

type SortDir = 'asc' | 'desc';

/** Rank used for the default confidence-desc sort (high → medium → prefix → excluded). */
const CONFIDENCE_RANK: Record<ClusterConfidence, number> = {
  high: 4,
  medium: 3,
  prefix_variant: 2,
  variant_excluded: 1,
};

const CONFIDENCE_META: Record<
  ClusterConfidence,
  { label: string; variant: 'success' | 'warning' | 'info' | 'amber' }
> = {
  high: { label: 'High', variant: 'success' },
  medium: { label: 'Medium', variant: 'warning' },
  // Amber (not red): these aren't confirmed dupes — a reviewer decides whether to
  // merge the suffixed variants into the base or keep them as distinct variants.
  prefix_variant: { label: 'Prefix variant', variant: 'amber' },
  variant_excluded: { label: 'Variant (excluded)', variant: 'info' },
};

/** All confidence bands, in display order. */
const ALL_BANDS: ClusterConfidence[] = [
  'high',
  'medium',
  'prefix_variant',
  'variant_excluded',
];

/**
 * For a prefix_variant cluster, a compact "base ⊂ variant, variant…" summary so
 * a reviewer sees the prefix relationship at a glance. The base is the
 * recommended canonical (shortest-prefix ref); the rest are its suffixed
 * variants.
 */
function prefixSubsetSummary(cluster: DuplicateCluster): string {
  const baseDoc =
    cluster.docs.find((d) => d.referenceId === cluster.recommendedCanonicalId) ??
    cluster.docs[0];
  const base = baseDoc?.reference || baseDoc?.referenceId || '?';
  const variants = cluster.docs
    .filter((d) => d.referenceId !== baseDoc?.referenceId)
    .map((d) => d.reference || d.referenceId);
  if (variants.length === 0) return base;
  return `${base} ⊂ ${variants.join(', ')}`;
}

/** Stable identity for a cluster row (brand + identity key is unique per scan). */
function clusterKey(c: DuplicateCluster): string {
  return `${c.brandId}|${c.identityKey}`;
}

interface DuplicateClusterTableProps {
  clusters: DuplicateCluster[];
  /** Open the Phase 2 merge dialog for a cluster. */
  onReview: (cluster: DuplicateCluster) => void;
  /**
   * Reviewer picked a different canonical doc for a cluster (the inline "Make
   * canonical" card action). The parent mutates the in-memory scan result's
   * `recommendedCanonicalId` for that cluster — no backend write.
   */
  onCanonicalChange: (cluster: DuplicateCluster, canonicalReferenceId: string) => void;
}

export function DuplicateClusterTable({
  clusters,
  onReview,
  onCanonicalChange,
}: DuplicateClusterTableProps) {
  // Default sort: confidence desc, doc count desc (handled in the comparator).
  const [sortKey, setSortKey] = useState<SortKey>('confidence');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Cluster keys whose canonical the reviewer has manually overridden this
  // session — drives the "Selected canonical" badge cue (vs the scan's
  // "Recommended canonical"). Local UI state; not persisted.
  const [overriddenKeys, setOverriddenKeys] = useState<Set<string>>(new Set());

  const handleMakeCanonical = (cluster: DuplicateCluster, docId: string) => {
    setOverriddenKeys((prev) => new Set(prev).add(clusterKey(cluster)));
    onCanonicalChange(cluster, docId);
  };

  // Which confidence bands are visible. All on by default.
  const [visibleBands, setVisibleBands] = useState<Set<ClusterConfidence>>(
    new Set<ClusterConfidence>(ALL_BANDS),
  );

  const bandCounts = useMemo(() => {
    const counts: Record<ClusterConfidence, number> = {
      high: 0,
      medium: 0,
      prefix_variant: 0,
      variant_excluded: 0,
    };
    for (const c of clusters) counts[c.confidence] += 1;
    return counts;
  }, [clusters]);

  const filtered = useMemo(
    () => clusters.filter((c) => visibleBands.has(c.confidence)),
    [clusters, visibleBands],
  );

  const sorted = useMemo(() => {
    const factor = sortDir === 'asc' ? 1 : -1;
    const value = (c: DuplicateCluster): number | string => {
      switch (sortKey) {
        case 'brandId':
          return c.brandId;
        case 'identityKey':
          return c.identityKey;
        case 'docCount':
          return c.docs.length;
        case 'confidence':
          return CONFIDENCE_RANK[c.confidence];
        case 'bookmarks':
          return c.pointerCounts.bookmarks;
        case 'wishlists':
          return c.pointerCounts.wishlists;
        case 'curatedCollections':
          return c.pointerCounts.curatedCollections;
        case 'submissionsIndex':
          return c.pointerCounts.submissionsIndex;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv);
      } else {
        cmp = (av as number) - (bv as number);
      }
      cmp *= factor;
      // Tiebreaker (direction-independent): doc count desc, then identity key
      // asc — keeps the default "confidence desc, doc count desc" ordering and
      // gives every column a stable, intuitive secondary sort.
      if (cmp === 0 && sortKey !== 'docCount') cmp = b.docs.length - a.docs.length;
      if (cmp === 0) cmp = a.identityKey.localeCompare(b.identityKey);
      return cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Text columns default ascending; numeric/confidence default descending.
      setSortDir(key === 'brandId' || key === 'identityKey' ? 'asc' : 'desc');
    }
  };

  const toggleBand = (band: ClusterConfidence) => {
    setVisibleBands((prev) => {
      const next = new Set(prev);
      if (next.has(band)) next.delete(band);
      else next.add(band);
      return next;
    });
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Confidence filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Show
        </span>
        {ALL_BANDS.map((band) => {
          const active = visibleBands.has(band);
          const meta = CONFIDENCE_META[band];
          return (
            <button
              key={band}
              type="button"
              onClick={() => toggleBand(band)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-brand-50 border-brand-300 text-brand-700'
                  : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-gray-600'
              }`}
            >
              {meta.label}
              <span className="ml-1.5 text-gray-400">({bandCounts[band]})</span>
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-8" />
              <SortableHeader label="Brand" columnKey="brandId" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Identity key" columnKey="identityKey" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Docs" columnKey="docCount" align="right" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Confidence" columnKey="confidence" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Recommended canonical
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Variant reason</th>
              <SortableHeader label="Bkmk" columnKey="bookmarks" align="right" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Wish" columnKey="wishlists" align="right" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Curated" columnKey="curatedCollections" align="right" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableHeader label="Subm" columnKey="submissionsIndex" align="right" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2 text-right font-medium text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-gray-400">
                  No clusters match the current filters.
                </td>
              </tr>
            )}
            {sorted.map((cluster) => {
              const key = clusterKey(cluster);
              const isOpen = expanded.has(key);
              const meta = CONFIDENCE_META[cluster.confidence];
              return (
                <ClusterRows
                  key={key}
                  cluster={cluster}
                  isOpen={isOpen}
                  badgeLabel={meta.label}
                  badgeVariant={meta.variant}
                  isOverridden={overriddenKeys.has(key)}
                  onToggle={() => toggleExpanded(key)}
                  onReview={() => onReview(cluster)}
                  onMakeCanonical={(docId) => handleMakeCanonical(cluster, docId)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  /** The column this header sorts by. */
  columnKey: SortKey;
  /** The table's currently-active sort key. */
  activeKey: SortKey;
  dir: SortDir;
  align?: 'left' | 'right';
  onSort: (key: SortKey) => void;
}

function SortableHeader({
  label,
  columnKey,
  activeKey,
  dir,
  align = 'left',
  onSort,
}: SortableHeaderProps) {
  const active = activeKey === columnKey;
  return (
    <th
      className={`px-3 py-2 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => onSort(columnKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-gray-300">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </span>
    </th>
  );
}

interface ClusterRowsProps {
  cluster: DuplicateCluster;
  isOpen: boolean;
  badgeLabel: string;
  badgeVariant: 'success' | 'warning' | 'info' | 'amber';
  /** True once the reviewer has manually overridden this cluster's canonical. */
  isOverridden: boolean;
  onToggle: () => void;
  onReview: () => void;
  /** Reviewer clicked "Make canonical" on a non-canonical doc card. */
  onMakeCanonical: (docId: string) => void;
}

function ClusterRows({
  cluster,
  isOpen,
  badgeLabel,
  badgeVariant,
  isOverridden,
  onToggle,
  onReview,
  onMakeCanonical,
}: ClusterRowsProps) {
  const { pointerCounts } = cluster;
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <td className="px-2 py-2 text-gray-400">
          <span className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>
            ▶
          </span>
        </td>
        <td className="px-3 py-2 text-gray-700">{cluster.brandId}</td>
        <td className="px-3 py-2 font-mono text-gray-900">{cluster.identityKey}</td>
        <td className="px-3 py-2 text-right text-gray-700">{cluster.docs.length}</td>
        <td className="px-3 py-2">
          <Badge variant={badgeVariant}>{badgeLabel}</Badge>
        </td>
        <td className="px-3 py-2 font-mono text-xs text-gray-600">
          {cluster.recommendedCanonicalId ?? <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2 text-xs text-gray-500">
          {cluster.confidence === 'prefix_variant' ? (
            <span className="font-mono text-amber-700">
              {prefixSubsetSummary(cluster)}
            </span>
          ) : (
            cluster.variantExclusionReason ?? <span className="text-gray-300">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-right text-gray-600">{pointerCounts.bookmarks}</td>
        <td className="px-3 py-2 text-right text-gray-600">{pointerCounts.wishlists}</td>
        <td className="px-3 py-2 text-right text-gray-600">{pointerCounts.curatedCollections}</td>
        <td className="px-3 py-2 text-right text-gray-600">{pointerCounts.submissionsIndex}</td>
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReview();
            }}
            className="rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
          >
            Review &amp; merge
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={12} className="bg-gray-50 px-4 py-4">
            <ClusterDetail
              cluster={cluster}
              isOverridden={isOverridden}
              onReview={onReview}
              onMakeCanonical={onMakeCanonical}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Side-by-side per-doc detail shown when a cluster row is expanded. */
function ClusterDetail({
  cluster,
  isOverridden,
  onReview,
  onMakeCanonical,
}: {
  cluster: DuplicateCluster;
  isOverridden: boolean;
  onReview: () => void;
  onMakeCanonical: (docId: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-700">
          {cluster.docs.length} documents in this cluster
        </h4>
        <button
          type="button"
          onClick={onReview}
          className="rounded-md border border-brand-300 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
        >
          Review &amp; merge →
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cluster.docs.map((doc) => (
          <DocCard
            key={doc.referenceId}
            doc={doc}
            isRecommended={doc.referenceId === cluster.recommendedCanonicalId}
            isOverridden={isOverridden}
            onMakeCanonical={() => onMakeCanonical(doc.referenceId)}
          />
        ))}
      </div>
    </div>
  );
}

function DocCard({
  doc,
  isRecommended,
  isOverridden,
  onMakeCanonical,
}: {
  doc: ScoredCandidate;
  isRecommended: boolean;
  /** True when the cluster's canonical was overridden by the reviewer. */
  isOverridden: boolean;
  onMakeCanonical: () => void;
}) {
  const d = doc.divergence;
  // `model` isn't declared on ScoredCandidate (the scan response stays
  // backward-compatible, like identityField/brandSummaries), but model-keyed
  // brands — e.g. Seiko with dedupIdentityField: 'model', where `reference` is a
  // shared caseback marking — differentiate cluster members by it. Read it
  // defensively so the card shows it whenever the scan provides it.
  const model = (doc as { model?: string }).model?.trim() ?? '';
  return (
    <div
      className={`rounded-lg border bg-white p-3 text-xs ${
        isRecommended ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[11px] text-gray-500 truncate" title={doc.referenceId}>
            {doc.referenceId}
          </div>
          <div className="font-medium text-gray-900">{doc.reference || '—'}</div>
          <div className="mt-0.5 text-[11px] text-gray-600">
            Model: <span className={model ? 'text-gray-900' : 'text-gray-300'}>{model || '—'}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-semibold text-gray-900">{doc.score}</div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400">score</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {isRecommended && (
          <Badge variant="success">
            {isOverridden ? 'Selected canonical' : 'Recommended canonical'}
          </Badge>
        )}
        {d.isLegacyShape && <Badge variant="warning">Legacy shape</Badge>}
        <Badge variant={d.hasReferenceNormalized ? 'default' : 'danger'}>
          {d.hasReferenceNormalized ? 'has refNormalized' : 'no refNormalized'}
        </Badge>
        <Badge variant={d.hasImages ? 'info' : 'default'}>
          {d.hasImages ? 'has images' : 'no images'}
        </Badge>
        <Badge variant="default">{d.populatedFieldCount} fields</Badge>
      </div>

      {/* Score breakdown */}
      {Object.keys(doc.scoreBreakdown ?? {}).length > 0 && (
        <div className="mt-2 text-[11px] text-gray-500">
          <span className="font-medium text-gray-600">Score: </span>
          {Object.entries(doc.scoreBreakdown)
            .map(([k, v]) => `${k} ${v >= 0 ? '+' : ''}${v}`)
            .join(', ')}
        </div>
      )}

      {/* Variant-relevant divergence fields */}
      <dl className="mt-2 space-y-0.5 border-t border-gray-100 pt-2">
        <DivRow label="Dial/hands color" value={fmtArr(d.dialAndHandsColor)} />
        <DivRow label="Strap material" value={d.strapMaterial} />
        <DivRow label="Strap color" value={d.strapColor} />
        <DivRow label="Case material" value={d.caseMaterial} />
        <DivRow label="Bezel material" value={d.bezelMaterial} />
        <DivRow label="Bezel finish" value={d.bezelFinish} />
      </dl>

      {(d.createdAtMs || d.updatedAtMs) && (
        <div className="mt-2 text-[10px] text-gray-400">
          {d.createdAtMs && <>created {fmtDate(d.createdAtMs)} · </>}
          {d.updatedAtMs && <>updated {fmtDate(d.updatedAtMs)}</>}
        </div>
      )}

      {/* Inline canonical override — only on non-canonical cards. Deliberately
          subtle (ghost button) so it doesn't compete with Review & merge. */}
      {!isRecommended && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <button
            type="button"
            onClick={onMakeCanonical}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            Make canonical
          </button>
        </div>
      )}
    </div>
  );
}

function DivRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-gray-400">{label}</dt>
      <dd className={value ? 'text-gray-800' : 'text-gray-300'}>{value || '—'}</dd>
    </div>
  );
}

function fmtArr(arr: string[] | null): string | null {
  if (!arr || arr.length === 0) return null;
  return arr.join(', ');
}

function fmtDate(ms: number): string {
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return String(ms);
  }
}
